import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  Target,
  ArrowLeft,
  CheckCircle2,
  Calendar,
  Compass,
  BookOpen,
  HeartHandshake,
  Sparkles,
  HandCoins,
  Stethoscope,
  Coins,
  Sun,
  Users,
} from "lucide-react";
import {
  SITE,
  CORE_PURPOSES,
  SHORT_TERM_GOALS,
  LONG_TERM_GOALS,
  FUNDING_STRATEGY,
  MONITORING,
} from "../lib/site";

const ICON_MAP = {
  BookOpen,
  HeartHandshake,
  Sparkles,
  HandCoins,
  Stethoscope,
  Coins,
  Sun,
  Users,
};

export default function MissionPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <main className="bg-[#FAF5F0] min-h-screen">
      {/* HERO */}
      <section
        data-testid="mission-hero"
        className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 bg-[#064E3B] text-white overflow-hidden"
      >
        <div className="absolute inset-0 geo-overlay-light opacity-40 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-[#065F46] blur-3xl opacity-60" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#0b6b4f] blur-3xl opacity-40" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="mission-back-link"
            className="inline-flex items-center gap-2 text-[#FCD34D] text-xs sm:text-sm font-semibold tracking-wide hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>

          <div className="mt-6 text-[11px] uppercase tracking-[0.24em] font-semibold text-[#FCD34D]">
            Strategic Plan • 2025 – 2035
          </div>
          <div className="mt-4 w-16 h-[3px] rounded-full bg-gradient-to-r from-[#FCD34D] to-[#DC2626]" />
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] font-semibold">
            Our Mission &{" "}
            <span className="italic text-[#FCD34D]">our promise</span>.
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-xl text-white/85 leading-relaxed">
            Alisaafaat Welfare Foundation is committed to addressing the spiritual,
            educational, and socioeconomic needs of the community — through projects
            that are sustainable, ethical, and deeply rooted in Islamic values.
          </p>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
            <div
              data-testid="mission-vision-card"
              className="rounded-3xl bg-[#FAF5F0] border border-[#E5E0DA] p-8 sm:p-10 relative overflow-hidden"
            >
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[#064E3B]/10 text-[#064E3B] mb-6">
                <Eye size={22} strokeWidth={1.75} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#064E3B] mb-2">
                Our Vision
              </div>
              <p className="font-serif-display text-3xl sm:text-4xl text-[#111827] leading-tight font-semibold">
                "{SITE.vision}"
              </p>
            </div>
            <div
              data-testid="mission-mission-card"
              className="rounded-3xl bg-[#064E3B] text-white p-8 sm:p-10 relative overflow-hidden"
            >
              <div className="absolute inset-0 geo-overlay-light opacity-40" />
              <div className="relative">
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-white/15 text-[#FCD34D] mb-6">
                  <Target size={22} strokeWidth={1.75} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D] mb-2">
                  Our Mission
                </div>
                <p className="font-serif-display text-3xl sm:text-4xl leading-tight font-semibold italic">
                  "{SITE.mission}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE PURPOSES */}
      <section
        data-testid="mission-core-purposes"
        className="py-16 sm:py-24 bg-[#FAF5F0]"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#064E3B]">
              Core Purposes
            </div>
            <div className="pill-divider mt-4" />
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-6 leading-[1.1] text-[#111827] font-semibold">
              Eight commitments,{" "}
              <span className="italic text-[#DC2626]">one ummah.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {CORE_PURPOSES.map((p, i) => {
              const Icon = ICON_MAP[p.icon] || CheckCircle2;
              return (
                <div
                  key={p.text}
                  data-testid={`purpose-${i}`}
                  className="group rounded-3xl bg-white border border-[#E5E0DA] p-6 sm:p-7 transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[#064E3B]/10 text-[#064E3B] group-hover:bg-[#064E3B] group-hover:text-white transition-colors">
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                    <span className="font-serif-display italic text-2xl text-[#E5E0DA] group-hover:text-[#D4AF37] transition-colors">
                      0{i + 1}
                    </span>
                  </div>
                  <p className="text-[#374151] leading-relaxed text-[15px]">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GOALS */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#064E3B]">
              Roadmap
            </div>
            <div className="pill-divider mt-4" />
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-6 leading-[1.1] text-[#111827] font-semibold">
              Short-term &{" "}
              <span className="italic text-[#064E3B]">long-term goals.</span>
            </h2>
          </div>

          {/* Short-Term */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <Calendar size={18} className="text-[#DC2626]" />
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#111827] font-semibold">
                Short-term (2025 – 2027)
              </h3>
            </div>
            <GoalsTable goals={SHORT_TERM_GOALS} testid="short-term-goals" />
          </div>

          {/* Long-Term */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Compass size={18} className="text-[#064E3B]" />
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#111827] font-semibold">
                Long-term (2028 – 2035)
              </h3>
            </div>
            <GoalsTable goals={LONG_TERM_GOALS} testid="long-term-goals" />
          </div>
        </div>
      </section>

      {/* FUNDING + MONITORING */}
      <section className="py-16 sm:py-24 bg-[#FAF5F0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16 grid md:grid-cols-2 gap-5 sm:gap-6">
          <div
            data-testid="funding-card"
            className="rounded-3xl bg-white border border-[#E5E0DA] p-8 sm:p-10"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#064E3B] mb-2">
              Funding & Sustainability
            </div>
            <h3 className="font-serif-display text-3xl sm:text-4xl text-[#111827] font-semibold leading-tight">
              How we'll grow,{" "}
              <span className="italic text-[#DC2626]">sustainably.</span>
            </h3>
            <ul className="mt-6 space-y-3">
              {FUNDING_STRATEGY.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[#374151]">
                  <CheckCircle2
                    size={18}
                    className="text-[#064E3B] shrink-0 mt-0.5"
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            data-testid="monitoring-card"
            className="rounded-3xl bg-[#064E3B] text-white p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute inset-0 geo-overlay-light opacity-40" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D] mb-2">
                Monitoring & Evaluation
              </div>
              <h3 className="font-serif-display text-3xl sm:text-4xl font-semibold leading-tight">
                Transparent,{" "}
                <span className="italic text-[#FCD34D]">accountable.</span>
              </h3>
              <ul className="mt-6 space-y-3">
                {MONITORING.map((m) => (
                  <li key={m} className="flex items-start gap-3 text-white/90">
                    <CheckCircle2
                      size={18}
                      className="text-[#FCD34D] shrink-0 mt-0.5"
                    />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONCLUSION */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#064E3B]">
            In closing
          </div>
          <div className="pill-divider mt-4 mx-auto" />
          <p className="font-serif-display text-2xl sm:text-4xl mt-8 leading-[1.25] text-[#111827] italic">
            "With the help of Allah and the support of donors and volunteers, we
            hope to{" "}
            <span className="text-[#064E3B] not-italic font-semibold">
              serve humanity with dignity
            </span>{" "}
            and{" "}
            <span className="text-[#DC2626] not-italic font-semibold">
              create a society where everybody is valued
            </span>
            ."
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/#donate"
              data-testid="mission-donate-cta"
              className="cta-shimmer inline-flex items-center justify-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white px-7 py-3.5 text-sm font-semibold transition-all hover:scale-[1.02]"
            >
              Donate Now
            </Link>
            <Link
              to="/newsletter"
              data-testid="mission-newsletter-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[#E5E0DA] hover:border-[#064E3B] text-[#111827] hover:text-[#064E3B] px-7 py-3.5 text-sm font-semibold transition-colors"
            >
              Read our newsletter
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function GoalsTable({ goals, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl border border-[#E5E0DA] bg-[#FAF5F0] overflow-hidden"
    >
      {/* Desktop header */}
      <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-4 bg-[#064E3B] text-white text-[10px] uppercase tracking-[0.22em] font-semibold">
        <div className="col-span-3">Goal</div>
        <div className="col-span-4">Actions</div>
        <div className="col-span-2">Timeline</div>
        <div className="col-span-3">KPI</div>
      </div>
      <ul className="divide-y divide-[#E5E0DA]">
        {goals.map((g, i) => (
          <li
            key={g.goal}
            className="grid sm:grid-cols-12 gap-2 sm:gap-4 px-5 sm:px-6 py-5 sm:py-5 hover:bg-white transition-colors"
          >
            <div className="sm:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#064E3B] sm:hidden mb-1">
                Goal {i + 1}
              </div>
              <div className="font-serif-display text-lg sm:text-xl text-[#111827] font-semibold leading-tight">
                {g.goal}
              </div>
            </div>
            <div className="sm:col-span-4 text-[#374151] text-[14px] sm:text-[15px]">
              <span className="sm:hidden text-[10px] uppercase tracking-[0.18em] font-semibold text-[#064E3B] block mb-1">
                Actions
              </span>
              {g.actions}
            </div>
            <div className="sm:col-span-2 text-[#374151] text-[14px] sm:text-[15px]">
              <span className="sm:hidden text-[10px] uppercase tracking-[0.18em] font-semibold text-[#064E3B] block mb-1">
                Timeline
              </span>
              <span className="inline-flex items-center rounded-full bg-[#D4AF37]/15 text-[#7c5e10] px-2.5 py-1 text-xs font-semibold">
                {g.timeline}
              </span>
            </div>
            <div className="sm:col-span-3 text-[#374151] text-[14px] sm:text-[15px]">
              <span className="sm:hidden text-[10px] uppercase tracking-[0.18em] font-semibold text-[#064E3B] block mb-1">
                KPI
              </span>
              {g.kpi}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
